/**
 * HTTP 客户端 · axios 封装
 * - 请求拦截器：自动附 Bearer token
 * - 响应拦截器：统一 {data, error} 解包 → 成功返回 data，失败抛 BusinessError
 * - 401：用 refresh token 换新 access token 一次，重试原请求；失败则清空并回登录页
 */
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import {
  clearAuth,
  getAccessToken,
  getRefreshToken,
  updateAccessToken,
} from '@/auth/storage';

import { BusinessError, type ApiResponse } from './types';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) || '/api';

export const http: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30_000,
});

// ── 请求拦截器：加 token ──
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── refresh 并发合并 ──
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    const res = await axios.post<ApiResponse<{ accessToken: string; expiresIn: number }>>(
      `${API_BASE_URL}/auth/refresh`,
      { refreshToken },
      { timeout: 10_000 }
    );
    const token = res.data?.data?.accessToken;
    if (token) {
      updateAccessToken(token);
      return token;
    }
  } catch {
    /* fallthrough */
  }
  return null;
}

function onUnauthorized(): void {
  clearAuth();
  // 用事件解耦，App 层监听跳转登录页
  window.dispatchEvent(new Event('poet:unauthorized'));
}

// ── 响应拦截器：解包 + 错误归一 ──
http.interceptors.response.use(
  (response) => {
    const body = response.data as ApiResponse<unknown>;
    if (body && typeof body === 'object' && 'error' in body) {
      if (body.error) {
        throw new BusinessError(
          body.error.code,
          body.error.message,
          response.status,
          body.error.details,
        );
      }
      // 成功：返回 data（不是整个响应体）
      response.data = body.data;
    }
    return response;
  },
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const status = error.response?.status ?? 0;
    const body = error.response?.data;

    // 401 尝试 refresh 一次
    const originalConfig = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    if (status === 401 && originalConfig && !originalConfig._retry) {
      originalConfig._retry = true;
      refreshPromise ??= refreshAccessToken();
      const newToken = await refreshPromise;
      refreshPromise = null;
      if (newToken) {
        if (originalConfig.headers) {
          originalConfig.headers.Authorization = `Bearer ${newToken}`;
        }
        return http.request(originalConfig);
      }
      onUnauthorized();
    }

    // 其它失败：构造 BusinessError
    if (body && typeof body === 'object' && body.error) {
      throw new BusinessError(body.error.code, body.error.message, status, body.error.details);
    }
    throw new BusinessError(
      'NETWORK_ERROR',
      error.message || '网络请求失败',
      status,
      undefined,
    );
  },
);

/** 薄封装：直接拿 data，调用方不用再 .data */
export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const res = await http.get<T>(url, { params });
  return res.data;
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const res = await http.post<T>(url, body);
  return res.data;
}

export async function apiPut<T>(url: string, body?: unknown): Promise<T> {
  const res = await http.put<T>(url, body);
  return res.data;
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const res = await http.patch<T>(url, body);
  return res.data;
}

export async function apiDelete<T>(url: string): Promise<T> {
  const res = await http.delete<T>(url);
  return res.data;
}
